import React, { useEffect, useState } from "react";
import { useObjectState } from "@uidotdev/usehooks";
import { useQuery } from "@tanstack/react-query";

import { useAIStream } from "../../../../Hooks/useAiStream";
import { createPrompt, getPrompt, deletePrompt, updatePrompt } from "../../../../Api/services/ai";

import Grid from "../../../../Components/Grid";
import Button from "../../../../Components/Button";
import Spacer from "../../../../Components/Spacer";
import { Text } from "../../../../Components/Text";
import Input from "../../../../Components/Inputs/Text";
import Textarea from "../../../../Components/Inputs/Textarea";
import Multiselect from "../../../../Components/Inputs/Multiselect";
import { AutoGrowTextArea } from "../../../../Components/Inputs/AutoGrowTextArea";
import { PromptSelector } from "./internal/prompt-selector";
import { ModelSelector } from "../../../../Components/Selectors/ModelSelector";
import { getJiraTicket } from "../../../../Api/services/jira";

import css from "./styles.styl";

const PROMPT_OPTIONS = [{ id: "is-jira-prompt", label: "Is Jira Prompt" }];

export const PromptLibraryView = () => {
	const [model, setModel] = useState({ value: "claude-3-5-sonnet", label: "claude-3-5-sonnet" });
	const [prompt, setPrompt] = useState("");
	const [variables, setVariables] = useState([]);
	const [promptName, setPromptName] = useState("");
	const [promptOptions, setPromptOptions] = useState([]);
	const [selectedPrompt, setSelectedPrompt] = useState(null);
	const [variableValues, setVariableValues] = useObjectState({});

	const { data, makeRequest } = useAIStream({ url: "/ai" });

	const { data: selectedPromptObject } = useQuery({
		queryKey: ["ai-prompt", selectedPrompt?.promptId, selectedPrompt?.version],
		queryFn: () => getPrompt(selectedPrompt.promptId, selectedPrompt.version),
		enabled: !!selectedPrompt,
	});

	/**
	 * Listen to changes on the prompt. Find any prompt variables that are
	 * surrounded in double curly braces. For each prompt variable, add it to
	 * the variables array.
	 */
	useEffect(() => {
		if (promptOptions.includes("is-jira-prompt")) {
			setVariables(["Jira Ticket"]);
			return;
		}

		const matches = prompt.match(/{{(.*?)}}/g) || [];
		const newVariables = matches.map((match) => match.replace(/[{}]/g, ""));
		setVariables([...new Set(newVariables)]);
	}, [prompt, promptOptions]);

	/**
	 * Listen to changes on the selected prompt object. If the selected prompt
	 * object changes, update the prompt with the new prompt value.
	 */
	useEffect(() => {
		if (selectedPromptObject) {
			setPrompt(selectedPromptObject.prompt);
			setPromptName(selectedPromptObject.name);
			setPromptOptions(selectedPromptObject.is_jira_prompt ? ["is-jira-prompt"] : []);
		}
	}, [selectedPromptObject]);

	const updateVariableValue = (variable, value) => {
		setVariableValues(() => ({
			[variable]: value,
		}));
	};

	/**
	 * Run the prompt. If the prompt is a Jira prompt, get the Jira ticket and
	 * insert the variables into the prompt. Otherwise, just use the variables
	 * as they are.
	 */
	const runPrompt = async () => {
		let promptVariables = {};

		if (promptOptions.includes("is-jira-prompt")) {
			promptVariables["Jira Ticket"] = variableValues["Jira Ticket"];

			const jiraTicket = await getJiraTicket(variableValues["Jira Ticket"]);

			/**
			 * Remap the variables to the following format:
			 * - all uppercase
			 * - prefix with "JIRA_"
			 */
			promptVariables = Object.keys(jiraTicket).reduce((acc, key) => {
				acc[`JIRA_${key.toUpperCase()}`] = jiraTicket[key];
				return acc;
			}, {});
		} else {
			promptVariables = variables.reduce((acc, variable) => {
				acc[variable] = variableValues[variable];
				return acc;
			}, {});
		}

		makeRequest({ modelId: model.value, prompt, variables: promptVariables });
	};

	const handleSavePrompt = async () => {
		await createPrompt({ name: promptName, prompt, isJiraPrompt: promptOptions.includes("is-jira-prompt") });
	};

	const handleUpdatePrompt = async () => {
		await updatePrompt(selectedPrompt.promptId, {
			name: promptName,
			prompt,
			isJiraPrompt: promptOptions.includes("is-jira-prompt"),
		});
	};

	const handleDeletePrompt = async () => {
		await deletePrompt(selectedPrompt.promptId);
	};

	const handleCreateNew = () => {
		setPrompt("");
		setPromptName("");
		setSelectedPrompt(null);
	};

	return (
		<div className={css.container}>
			<Grid gap={24} columns={["1fr", "1fr"]}>
				<div>
					<PromptSelector value={selectedPrompt} onChange={(value) => setSelectedPrompt(value)} />
					<Spacer size={2} />

					<div>
						<Text variant="caption" weight="bold">
							Prompt Name
						</Text>
						<Input value={promptName} onChange={setPromptName} />
					</div>

					<Spacer size={2} />

					<div>
						<Text variant="caption" weight="bold">
							Prompt Options
						</Text>
						<Multiselect
							options={PROMPT_OPTIONS}
							value={promptOptions}
							onChange={(val) => setPromptOptions(val)}
						/>
					</div>

					<Spacer size={2} />

					<div>
						<Text variant="caption" weight="bold">
							Prompt
						</Text>
						<Textarea className={css.input} value={prompt} onChange={setPrompt} />
					</div>

					{variables.map((variable) => {
						return (
							<React.Fragment key={variable}>
								<Spacer size={2} />
								<div>
									<Text variant="caption" weight="bold">
										{variable}
									</Text>
									<Spacer size={1} />
									<AutoGrowTextArea
										onChange={(value) => updateVariableValue(variable, value)}
										value={variableValues[variable] ?? ""}
									/>
								</div>
							</React.Fragment>
						);
					})}
				</div>
				<div>
					<Grid columns={["1fr", "80px", "80px", "80px", "80px"]}>
						<ModelSelector value={model} onChange={setModel} />

						{selectedPrompt ? (
							<>
								<Button color="red" onClick={() => handleDeletePrompt()}>
									Delete
								</Button>
								<Button color="black" onClick={() => handleCreateNew()}>
									New
								</Button>
								<Button color="green" onClick={() => handleUpdatePrompt()}>
									Update
								</Button>
								<Button onClick={runPrompt}>Run</Button>
							</>
						) : (
							<>
								<div />
								<div />
								<Button color="green" onClick={() => handleSavePrompt()}>
									Save
								</Button>
								<Button onClick={runPrompt}>Run</Button>
							</>
						)}
					</Grid>

					<Spacer size={2} />

					<div className={css.response}>
						<div>{data}</div>
					</div>
				</div>
			</Grid>
		</div>
	);
};
